import type {Page} from '@PosterWhiteboard/page/page.class';
import type {ItemType} from '@PosterWhiteboard/items/item/item.types';
import type {FabricImage, FabricObject} from '@postermywall/fabricjs-2';
import {ActiveSelection, Group, Point} from '@postermywall/fabricjs-2';
import {getUniqueString} from '@Utils/string.util';
import {degreesToRadians} from '@Utils/math.util';
import {cloneAsAlignedImage} from '@Utils/fabric.util';
import {omitKey} from '@Utils/record.util';
import type {DeepPartial} from '@/global';

export interface GroupObject {
  angle: number;
}

export interface PageGroupsObject {
  groupsHashMap: Record<string, GroupObject>;
}

export class PageGroups {
  private page: Page;
  public groupsHashMap: Record<string, GroupObject> = {};

  public constructor(page: Page) {
    this.page = page;
  }

  public toObject(): PageGroupsObject {
    return {groupsHashMap: {...this.groupsHashMap}};
  }

  public updateFromObject(obj: DeepPartial<PageGroupsObject> | undefined): void {
    if (!obj?.groupsHashMap) {
      return;
    }
    const next: Record<string, GroupObject> = {};
    for (const [id, record] of Object.entries(obj.groupsHashMap)) {
      if (record?.angle !== undefined) {
        next[id] = {angle: record.angle};
      }
    }
    this.groupsHashMap = next;
  }

  public updateGroup(groupId: string, data: Partial<GroupObject>): void {
    this.groupsHashMap[groupId] = {...this.groupsHashMap[groupId], ...data};
  }

  public getItemsByGroupId(groupId: string): ItemType[] {
    return this.page.items.getItemsInOrder().filter((item) => item.groupId === groupId);
  }

  public getGroupIdForFabricObjects(fabricObjects: FabricObject[]): string | undefined {
    const items: ItemType[] = [];
    for (const fo of fabricObjects) {
      const item = this.page.getItemForFabricObject(fo);
      if (!item) {
        return undefined;
      }
      items.push(item);
    }
    return this.getGroupIdForItems(items);
  }

  public getGroupIdForCurrentSelection(): string | undefined {
    if (!this.isCurrentSelectionGroupable()) {
      return undefined;
    }
    return this.getGroupIdForItems(this.page.getSelectedItems());
  }

  private getGroupIdForItems(items: ItemType[]): string | undefined {
    if (items.length < 2) {
      return undefined;
    }
    const firstGroupId = items[0].groupId;
    if (!firstGroupId) {
      return undefined;
    }
    if (!items.every((item) => item.groupId === firstGroupId)) {
      return undefined;
    }
    if (items.length !== this.getItemsByGroupId(firstGroupId).length) {
      return undefined;
    }
    return firstGroupId;
  }

  public isSelectionExactlyOneGroup(): boolean {
    return this.getGroupIdForCurrentSelection() !== undefined;
  }

  public isCurrentSelectionGroupable(): boolean {
    const movable = this.page.getSelectedZIndexMovableItems();
    return movable.length >= 2 && movable.length === this.page.getSelectedItems().length;
  }

  public getGroupZRange(groupId: string): {bottomZ: number; topZ: number} | undefined {
    const objs = this.page.fabricCanvas.getObjects();
    const indices = this.getItemsByGroupId(groupId)
      .map((m) => objs.indexOf(m.fabricObject))
      .filter((i) => i >= 0);
    if (indices.length === 0) {
      return undefined;
    }
    return {bottomZ: Math.min(...indices), topZ: Math.max(...indices)};
  }

  public groupSelection(): void {
    if (!this.isCurrentSelectionGroupable()) {
      return;
    }
    this.groupItems(this.page.getSelectedItems());
  }

  public ungroupSelection(): void {
    const selectedItems = this.page.getSelectedItems();
    if (selectedItems.length === 1 && selectedItems[0].groupId) {
      this.removeItemFromGroup(selectedItems[0]);
      return;
    }

    const exclusiveGroupId = this.getGroupIdForCurrentSelection();
    if (exclusiveGroupId) {
      this.ungroupItems(exclusiveGroupId);
    }
  }

  public groupItems(items: ItemType[]): string | undefined {
    if (items.length < 2) {
      return undefined;
    }

    const groupId = getUniqueString();
    for (const item of items) {
      item.groupId = groupId;
    }

    this.enforceGroupContiguity(groupId);
    this.page.groupDrillIn.enableSubTargetCheck();
    this.page.poster.itemsMultiSelect.updateFromObject({enabled: false});

    this.page.poster.history.addPosterHistory();
    this.page.poster.redux.updateReduxData();
    this.page.fire('modified');

    return groupId;
  }

  public ungroupItems(groupId: string): void {
    const members = this.getItemsByGroupId(groupId);
    if (members.length === 0) {
      return;
    }

    for (const member of members) {
      member.groupId = null;
    }
    this.groupsHashMap = omitKey(this.groupsHashMap, groupId);

    this.page.poster.itemsMultiSelect.updateFromObject({enabled: false});

    this.page.poster.history.addPosterHistory();
    this.page.poster.redux.updateReduxData();
    this.page.fire('modified');
  }

  public removeItemFromGroup(item: ItemType): void {
    if (!item.groupId) {
      return;
    }
    const groupId = item.groupId;
    item.groupId = null;

    const remainingMembers = this.getItemsByGroupId(groupId);
    if (remainingMembers.length >= 2) {
      const canvas = this.page.fabricCanvas;
      const objs = canvas.getObjects();
      const aIndex = objs.indexOf(item.fabricObject);
      const maxRemaining = Math.max(...remainingMembers.map((m) => objs.indexOf(m.fabricObject)));
      // Fabric's moveObjectTo with target >= source lands at exactly that slot after its
      // remove+insert reindex — placing the leaver one above the topmost remaining member.
      if (aIndex >= 0 && aIndex < maxRemaining) {
        canvas.moveObjectTo(item.fabricObject, maxRemaining);
      }
    }

    this.enforceGroupContiguity(groupId);
    this.autoDissolveIfTooSmall(groupId);

    this.page.poster.history.addPosterHistory();
    this.page.poster.redux.updateReduxData();
    this.page.fire('modified');
  }

  public repairAllGroupContiguity(): void {
    const seen = new Set<string>();
    for (const item of Object.values(this.page.items.itemsHashMap)) {
      if (item.groupId && !seen.has(item.groupId)) {
        seen.add(item.groupId);
        this.enforceGroupContiguity(item.groupId);
      }
    }
  }

  private enforceGroupContiguity(groupId: string): void {
    const members = this.getItemsByGroupId(groupId);
    if (members.length < 2) {
      return;
    }

    const canvas = this.page.fabricCanvas;
    const canvasObjects = canvas.getObjects();

    const memberIndices = members
      .map((item) => ({item, index: canvasObjects.indexOf(item.fabricObject)}))
      .filter(({index}) => index >= 0)
      .sort((a, b) => a.index - b.index);

    if (memberIndices.length < 2) {
      return;
    }

    const frontmostIndex = memberIndices[memberIndices.length - 1].index;

    // Top-down iteration is load-bearing: moving frontmost-of-the-rest first avoids
    // intermediate states where a non-member gets wedged between members.
    for (let i = memberIndices.length - 2; i >= 0; i--) {
      const offset = memberIndices.length - 1 - i;
      canvas.moveObjectTo(memberIndices[i].item.fabricObject, frontmostIndex - offset);
    }
  }

  public autoDissolveIfTooSmall(groupId: string): void {
    const members = this.getItemsByGroupId(groupId);
    if (members.length >= 2) {
      return;
    }

    for (const member of members) {
      member.groupId = null;
    }
    this.groupsHashMap = omitKey(this.groupsHashMap, groupId);
  }

  public getBoundingBoxForGroup(groupId: string): {left: number; top: number; width: number; height: number; angle: number} | undefined {
    const members = this.getItemsByGroupId(groupId);
    if (members.length < 2) {
      return undefined;
    }
    const angle = this.groupsHashMap[groupId]?.angle ?? 0;
    const rad = degreesToRadians(angle);
    const c = Math.cos(rad);
    const s = Math.sin(rad);
    let xMin = Number.POSITIVE_INFINITY;
    let xMax = Number.NEGATIVE_INFINITY;
    let yMin = Number.POSITIVE_INFINITY;
    let yMax = Number.NEGATIVE_INFINITY;
    for (const member of members) {
      const fo = member.fabricObject;
      const corners = fo.getCornerPoints(fo.getCenterPoint());
      for (const corner of [corners.tl, corners.tr, corners.br, corners.bl]) {
        const ux = c * corner.x + s * corner.y;
        const uy = -s * corner.x + c * corner.y;
        if (ux < xMin) xMin = ux;
        if (ux > xMax) xMax = ux;
        if (uy < yMin) yMin = uy;
        if (uy > yMax) yMax = uy;
      }
    }
    const midX = (xMin + xMax) / 2;
    const midY = (yMin + yMax) / 2;
    const width = xMax - xMin;
    const height = yMax - yMin;
    const pivot = new Point(c * midX - s * midY, s * midX + c * midY);
    return {left: pivot.x - width / 2, top: pivot.y - height / 2, width, height, angle};
  }

  public createActiveSelectionForGroup(fabricObjects: FabricObject[], groupId: string): ActiveSelection {
    const bbox = this.getBoundingBoxForGroup(groupId);
    if (bbox && bbox.angle !== 0) {
      const pivot = new Point(bbox.left + bbox.width / 2, bbox.top + bbox.height / 2);
      this.unrotateChildrenAroundPivot(fabricObjects, bbox.angle, pivot);
    }
    const selection = new ActiveSelection(fabricObjects, {canvas: this.page.fabricCanvas});
    if (bbox && bbox.angle !== 0) {
      selection.rotate(bbox.angle);
      selection.setCoords();
    }
    return selection;
  }

  private unrotateChildrenAroundPivot(fabricObjects: FabricObject[], angleDeg: number, pivot: Point): void {
    const rad = degreesToRadians(-angleDeg);
    for (const fo of fabricObjects) {
      const rotated = new Point(fo.left, fo.top).rotate(rad, pivot);
      fo.left = rotated.x;
      fo.top = rotated.y;
      fo.angle -= angleDeg;
      fo.setCoords();
    }
  }

  public async toDataURL(groupId: string, {maxDimension}: {maxDimension?: number} = {}): Promise<string | null> {
    const members = this.getItemsByGroupId(groupId);
    if (members.length === 0) {
      return null;
    }

    const clones = await Promise.all(members.map((member): Promise<FabricImage> => this.cloneGroupItemAsAlignedImage(member)));
    await Promise.all(clones.map((clone): Promise<void> => this.awaitImageDecode(clone)));

    const tempGroup = new Group(clones);
    let multiplier = 1;
    if (maxDimension) {
      const maxSide = Math.max(tempGroup.getScaledWidth(), tempGroup.getScaledHeight());
      if (maxSide > 0) {
        multiplier = maxDimension / maxSide;
      }
    }

    const url = tempGroup.toDataURL({
      format: 'png',
      multiplier,
    });
    tempGroup.dispose();
    for (const clone of clones) {
      clone.dispose();
    }
    return url;
  }

  private cloneGroupItemAsAlignedImage(item: ItemType): Promise<FabricImage> {
    const fabricObject = item.fabricObject;
    const visibility = fabricObject.visible;
    const left = fabricObject.left;
    const top = fabricObject.top;
    const angle = fabricObject.angle;
    const scaleX = fabricObject.scaleX;
    const scaleY = fabricObject.scaleY;
    fabricObject.set('visible', true);

    const isAdoptedBySelection = fabricObject.group instanceof ActiveSelection;
    if (isAdoptedBySelection) {
      const scenePosition = fabricObject.getXY();
      const totalAngle = fabricObject.getTotalAngle();
      const totalScaling = fabricObject.getObjectScaling();
      fabricObject.set({left: scenePosition.x, top: scenePosition.y, angle: totalAngle, scaleX: totalScaling.x, scaleY: totalScaling.y});
    }

    const clonePromise: Promise<FabricImage> = cloneAsAlignedImage(fabricObject);

    if (isAdoptedBySelection) {
      fabricObject.set({left, top, angle, scaleX, scaleY});
    }
    fabricObject.set('visible', visibility);
    return clonePromise;
  }

  private async awaitImageDecode(clone: FabricImage): Promise<void> {
    const el = clone.getElement();
    if (el instanceof HTMLImageElement && !el.complete) {
      try {
        await el.decode();
      } catch {
        // tolerate decode errors; group render will fall back gracefully
      }
    }
  }
}
